import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {TypeOfDateSettingsEnumAlarms} from "../../../../enums/dashboard/TypeOfDateSettingsEnumAlarms";
import {TimeUnitEnum} from "../../../../enums/dashboard/TimeUnitEnum";
import {BaseComponent} from "../../../BaseComponent";
import validationEngine from "devextreme/ui/validation_engine";
import {DatePipe} from "@angular/common";

@Component({
  selector: 'app-alarm-time-selector',
  templateUrl: './alarm-time-selector.component.html',
  styleUrls: ['./alarm-time-selector.component.css', '../baseAlarm.component.css']
})
export class AlarmTimeSelectorComponent extends BaseComponent implements OnInit {

  @Input('realtimeValue') realtimeValue: number = 1;

  @Input('realTimeUnit') realTimeUnit: TimeUnitEnum = TimeUnitEnum.HOURS;

  @Input('startDate') startDate: string | number | Date;

  @Input('endDate') endDate: string | number | Date;

  @Input('typeOfDateSettings') typeOfDateSettings: TypeOfDateSettingsEnumAlarms = TypeOfDateSettingsEnumAlarms.LAST_EVENTS;

  @Input('lastEventsNumber') lastEventsNumber!: number;

  @Output() realtimeValueChange: EventEmitter<number> = new EventEmitter();

  @Output() realTimeUnitChange: EventEmitter<TimeUnitEnum> = new EventEmitter();

  @Output() startDateChange: EventEmitter<string | number | Date> = new EventEmitter();

  @Output() endDateChange: EventEmitter<string | number | Date> = new EventEmitter();

  @Output() typeOfDateSettingsChange: EventEmitter<TypeOfDateSettingsEnumAlarms> = new EventEmitter();

  @Output() onTimeSettingChange: EventEmitter<void> = new EventEmitter();

  @Output() lastEventsNumberChange: EventEmitter<number> = new EventEmitter();

  protected realtimeValueBackup: number = 1;

  protected lastEventsNumberBackup: number = 20;

  protected realTimeUnitBackup: TimeUnitEnum = TimeUnitEnum.HOURS;

  protected startDateBackup: string | number | Date;

  protected endDateBackup: string | number | Date;

  protected typeOfDateSettingsBackup: TypeOfDateSettingsEnumAlarms = TypeOfDateSettingsEnumAlarms.LAST_EVENTS;

  protected dateIsValid: boolean = true;

  protected readonly TypeOfDateSettingsEnum = TypeOfDateSettingsEnumAlarms;

  protected typeOfDateSettingsList: string[] = Object.values(TypeOfDateSettingsEnumAlarms);

  protected today: string | number | Date;

  protected telemetrySearchUnitValues: string[];

  protected datesPopoverVisible: boolean = false;

  /**
   * Pipe used to translate timestamps in string dates
   * @protected
   */
  protected datePipe: DatePipe = inject(DatePipe);

  constructor() {
    super();
    this.today = new Date();
    this.endDate = new Date();
    this.endDateBackup = new Date();
    this.startDate = new Date(new Date().setDate(new Date().getDate() - 1));
    this.startDateBackup = new Date(new Date().setDate(new Date().getDate() - 1));
    this.telemetrySearchUnitValues = Object.values(TimeUnitEnum);
  }

  /**
   * Lambda function used to check whether the start date is over the end date
   */
  datesPopoverEndComparison = () => this.endDate;

  ngOnInit() {
    super.ngOnInit();
  }

  /**
   * Initializes timestamp of today and deepcopies the userMetricGroup
   */
  initializeToday() {
    this.today = new Date();
    return this.today;
  }

  /**
   * Check if the startDate is valid
   */
  checkStartDateValidity() {
    if (!!validationEngine.getGroupConfig('fromDate')) {
      this.dateIsValid = !!validationEngine.validateGroup('fromDate').isValid;
    }
  }

  applyDataChanges() {
    this.realtimeValueChange.emit(this.realtimeValue);
    this.realTimeUnitChange.emit(this.realTimeUnit);
    this.startDateChange.emit(this.startDate);
    this.endDateChange.emit(this.endDate);
    this.typeOfDateSettingsChange.emit(this.typeOfDateSettings);
    this.lastEventsNumberChange.emit(this.lastEventsNumber);
    this.backupValues();
    this.onTimeSettingChange.emit();
  }

  getAcquisitionCronLabel = (acquisitionCron: string) => {
    const val = this.translate.instant('DATE_SELECTION.REALTIME.PLURAL.' + acquisitionCron);
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }

  /**
   * Beautyfies and prints the dates in the correct format
   */
  printDateSelectionText() {
    if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.LAST_EVENTS) {
      return `<span>${this.translate.instant('DATE_SELECTION.LAST_EVENTS.TITLE')}:</span><span>${this.lastEventsNumber}</span>`;
    } else if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.FIXED_DATE) {
      return `<span>${this.datePipe.transform(this.startDate, 'dd/MM/yyyy HH:mm')}</span><span>${this.datePipe.transform(this.endDate, 'dd/MM/yyyy HH:mm')}</span>`;
    } else if (this.typeOfDateSettings === TypeOfDateSettingsEnumAlarms.REALTIME) {
      let translation = this.realtimeValue === 1 ? 'DATE_SELECTION.REALTIME.SINGULAR.' : 'DATE_SELECTION.REALTIME.PLURAL.';
      return `<span>${this.translate.instant('DATE_SELECTION.REALTIME.TITLE')}:</span><span>${this.realtimeValue + ' ' + this.translate.instant(translation + this.realTimeUnit)}</span>`;
    } else {
      return '';
    }
  }

  resetValues() {
    this.realtimeValue = this.appService.deepCopy(this.realtimeValueBackup);
    this.realTimeUnit = this.appService.deepCopy(this.realTimeUnitBackup);
    this.startDate = this.appService.deepCopy(this.startDateBackup);
    this.endDate = this.appService.deepCopy(this.endDateBackup);
    this.typeOfDateSettings = this.appService.deepCopy(this.typeOfDateSettingsBackup);
    this.lastEventsNumber = this.appService.deepCopy(this.lastEventsNumberBackup);

  }

  backupValues() {
    this.realtimeValueBackup = this.appService.deepCopy(this.realtimeValue);
    this.realTimeUnitBackup = this.appService.deepCopy(this.realTimeUnit);
    this.startDateBackup = this.appService.deepCopy(this.startDate);
    this.endDateBackup = this.appService.deepCopy(this.endDate);
    this.typeOfDateSettingsBackup = this.appService.deepCopy(this.typeOfDateSettings);
    this.lastEventsNumberBackup = this.appService.deepCopy(this.lastEventsNumber);
  }
}
